import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, type Denied, deny } from '../types';

/**
 * A pallet tag. Its one lifecycle lock is the server's delete refusal once the tag has been used.
 *
 *   view · add · edit ──────────────────────────▶ allow   (no state locks a tag's fields)
 *   delete ──▶ consumed? ── no ──▶ allow
 *                 └─ yes ──▶ ✗ PALLET_TAG_USED   "Used pallet tags can't be deleted."
 *
 *   decidePalletTagBatchDelete(per-tag delete decisions) ──▶ any refused ──▶ ✗ PALLET_TAG_USED
 *                                                 "Some selected pallet tags are used and can't be deleted." (one sentence per batch)
 *
 * "Used" is the tag's used amount — the negated sum of its quantity-adjustment deltas — above zero.
 * The api reads the sum with one SQL SUM, the web adds up the tag's `quantityAdjustments`; both go
 * through `palletTagUsedAmount` and `palletTagIsConsumed` so the two sides cannot drift (#7630).
 */
export type PalletTagFacts = {
  /** `palletTagIsConsumed(palletTagUsedAmount(Σ delta))` — some of the tag has been taken. */
  consumed: boolean;
};

/** No field of a tag is locked by its state, so the policy names none. The grid columns stay open. */
export const PALLET_TAG_FIELDS = [] as const;

export type PalletTagField = (typeof PALLET_TAG_FIELDS)[number];

/** Σ delta over a tag's quantity adjustments as the web holds them (a null delta adds nothing). */
export const palletTagDeltaSum = (adjustments: readonly { delta?: number | null }[]): number =>
  adjustments.reduce((sum, { delta }) => sum + (delta ?? 0), 0);

/** A tag's used amount: its delta sum, negated. `null` (no adjustments) reads as nothing used. */
export const palletTagUsedAmount = (deltaSum: number | null | undefined): number => (deltaSum ?? 0) * -1;

/** THE "has this tag been used" test, shared so the toolbar and the api refuse the same tags. */
export const palletTagIsConsumed = (usedAmount: number): boolean => usedAmount > 0;

const decideEntity = (facts: PalletTagFacts, action: Action<PalletTagField>): Decision => {
  if (action.type === 'delete' && facts.consumed) {
    return deny('PALLET_TAG_USED', "Used pallet tags can't be deleted.");
  }

  return allow();
};

export const palletTagPolicy: Policy<PalletTagFacts, PalletTagField> = definePolicy<PalletTagFacts, PalletTagField>({
  kind: 'pallet tag',
  fields: PALLET_TAG_FIELDS,
  ops: [],
  requires: {},
  entity: decideEntity,
});

/**
 * The bulk delete's ONE answer over its per-tag `delete` decisions (each asked with the caller's
 * actor): a used tag refuses the whole batch with the batch sentence; any other refusal passes
 * through unchanged. An empty batch is allowed — there is nothing to refuse.
 */
export const decidePalletTagBatchDelete = (decisions: readonly Decision[]): Decision => {
  const refusal = decisions.find((decision): decision is Denied => !decision.allowed);

  if (refusal === undefined) return allow();
  if (refusal.code !== 'PALLET_TAG_USED') return refusal;

  return deny('PALLET_TAG_USED', "Some selected pallet tags are used and can't be deleted.");
};
